import { useMemo } from "react";
import type { Control, SubmitHandler } from "react-hook-form";
import { useController, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Chip, Stack } from "@braintrust/braintrust-ui-components";
import { Typography } from "@braintrust/braintrust-ui-components";
import { StarFilledSVGIcon as StarIcon } from "@braintrust/braintrust-ui-components/Icons";
import { FilterPopoverFooter } from "@js/apps/employer/components/filter-popover/filter-popover-footer";
import { FilterPopoverFormLayoutWrapper } from "@js/apps/employer/components/filter-popover/filter-popover-form-layout-wrapper";
import type { RhfForm } from "@js/rhf/types";
import type { Job, JobSkill } from "@js/types/jobs";

const bidSkillsFilterFormSchema = z.object({
  skills: z.array(z.number()),
  top_skills: z.array(z.number()),
});

type FormType = RhfForm<typeof bidSkillsFilterFormSchema>;
export type BidSkillsFilterFormValues = FormType["Values"];

export type BidSkillsFilterFormProps = {
  job: Pick<Job, "job_skills">;
  currentSkills: BidSkillsFilterFormValues["skills"];
  currentTopSkills: BidSkillsFilterFormValues["top_skills"];
  onApply: (values: BidSkillsFilterFormValues) => void;
};

export const BidSkillsFilterForm = ({
  job,
  currentSkills,
  currentTopSkills,
  onApply,
}: BidSkillsFilterFormProps) => {
  const initialValues = useMemo(() => {
    return { skills: currentSkills, top_skills: currentTopSkills };
  }, [currentSkills, currentTopSkills]);

  const { handleSubmit, control, reset } = useForm<BidSkillsFilterFormValues>({
    resolver: zodResolver(bidSkillsFilterFormSchema),
    values: initialValues,
  });

  const onSubmit: SubmitHandler<BidSkillsFilterFormValues> = (values) => {
    onApply(values);
  };

  const onReset = () => {
    reset({ skills: [], top_skills: [] });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FilterPopoverFormLayoutWrapper
        formBody={<BidSkillsFilterFormBody job={job} control={control} />}
        formFooter={
          <FilterPopoverFooter
            onReset={onReset}
            applyButtonVariant="dark-violet"
          />
        }
      />
    </form>
  );
};

type SkillOptionChipProps = {
  jobSkill: JobSkill;
  isSelected: boolean;
  handleClick: (jobSkill: JobSkill) => void;
};

export const SkillOptionChip = ({
  jobSkill,
  handleClick,
  isSelected,
}: SkillOptionChipProps) => {
  return (
    <Chip
      role="checkbox"
      size="small"
      LabelProps={{ size: "small", variant: "label" }}
      onClick={() => handleClick(jobSkill)}
      color={isSelected ? "violet" : "grey-4"}
      sx={
        !isSelected
          ? {
              "&:hover": {
                border: "1px solid var(--medium-violet)",
                color: "var(--black)",
                backgroundColor: "var(--soft-violet)",
              },
            }
          : undefined
      }
      label={
        <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
          {!!jobSkill.is_top && <StarIcon sx={{ fontSize: "16px" }} />}
          {jobSkill.skill.name}
        </Stack>
      }
      aria-checked={!!isSelected}
    />
  );
};

type BidSkillsFilterFormBodyProps = {
  job: Pick<Job, "job_skills">;
  control: Control<BidSkillsFilterFormValues>;
};

const BidSkillsFilterFormBody = ({
  job,
  control,
}: BidSkillsFilterFormBodyProps) => {
  const { field: skillsField } = useController({
    name: "skills",
    control: control,
  });

  const { field: topSkillsField } = useController({
    name: "top_skills",
    control: control,
  });

  const handleSkillClick = (jobSkill: JobSkill) => {
    const field = jobSkill.is_top ? topSkillsField : skillsField;
    const clickedSkillId = jobSkill.skill.id;
    const isSelected = field.value.includes(clickedSkillId);
    if (isSelected) {
      field.onChange(
        field.value.filter((selectedSkill) => selectedSkill !== clickedSkillId),
      );
    } else {
      field.onChange([...field.value, clickedSkillId]);
    }
  };

  const sortedJobSkills = useMemo(() => {
    const sortedSkills = [...job.job_skills].sort((a, b) => {
      return Number(b.is_top) - Number(a.is_top);
    });

    return sortedSkills;
  }, [job.job_skills]);

  return (
    <Stack>
      <Typography component="h3" variant="label" size="large" sx={{ mb: 2 }}>
        Select Talent skills
      </Typography>
      <Typography component="p" variant="paragraph" size="small" sx={{ mb: 1 }}>
        Skills with a ★ were marked as most important by the client to improve
        match quality.
      </Typography>
      <Stack
        direction="row"
        sx={{
          gap: 1,
          flexWrap: "wrap",
          maxHeight: "min(50vh, 350px)",
          overflow: "auto",
          marginInline: -3, // place the scroll near the edge of the popover
          paddingInline: 3,
        }}
      >
        {sortedJobSkills.map((jobSkill) => {
          const field = jobSkill.is_top ? topSkillsField : skillsField;
          const isSelected = field.value.includes(jobSkill.skill.id);

          return (
            <SkillOptionChip
              key={jobSkill.id}
              jobSkill={jobSkill}
              isSelected={isSelected}
              handleClick={handleSkillClick}
            />
          );
        })}
      </Stack>
    </Stack>
  );
};
